'use client'

/**
 * Stage window follower (plans/07 5.3 and 5.3a; plans/08 4.6 and 12 item 1).
 * Mounted by the shell on every page and inert unless this window is the
 * stage (`sessionStorage["aio.stage"]` is `1`, set when the tab opens at
 * `/?stage=1` or is named `aio-stage`) or the Presenter's own window doubles
 * as the stage (`single`).
 *
 * The stage follows the server: when `story.seq` grows it takes the persona
 * and page of `story.stage` when set, otherwise the step's `persona` and
 * `opens`. A cue from the Presenter tab (localStorage `storage` event) makes
 * it follow at once instead of on the next poll. Shortcuts in this window:
 * Shift+Alt+Right Continue (never approves), Shift+Alt+O back to the step's
 * page, Shift+Alt+1 to 4 the step's "Show on stage" links, Shift+Alt+P back
 * to the Presenter (single window only).
 */

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { toast } from '@/components/ui/toast'
import { getChosenPersona, setPersona, type PersonaId } from '@/lib/persona'
import { useStory } from '@/lib/story'
import type { StoryState } from '@/lib/dr-types'
import { storyToken } from '@/lib/autopilot'
import { errorText } from '@/components/data-manager/dm-api'
import {
  STAGE_CUE_KEY,
  STAGE_HEARTBEAT_MS,
  STAGE_MODE_EVENT,
  STAGE_STORAGE_KEY as STAGE_KEY,
  STAGE_WINDOW_NAME,
  getStageMode,
  isStageShortcut,
  parseCue,
  safeAppHref,
  setStageMode,
  writeHeartbeat,
  type StageMode,
} from './stage-bus'
import { continueStory, fetchSupportStory, showLinkOnStage } from './useStoryControls'
import type { StageTarget } from './presenter-types'

/** sessionStorage key that marks a window as the stage window. */
export const STAGE_STORAGE_KEY = STAGE_KEY

const PRESENTER_PATH = '/presenter'

function currentHref(): string {
  return `${window.location.pathname}${window.location.search}`
}

/**
 * Shift+Alt+Right: Continue with the token of the wait this window shows (the
 * last poll), never a step move, so it approves nothing (plans/10 P5, 5.2). The
 * answer or the server's refusal shows under the header chip.
 */
async function continueFromStage(story: StoryState | null): Promise<void> {
  await continueStory(storyToken(story), 'shortcut', story?.autopilot?.next_title ?? null, 'header').catch(() => undefined)
}

/** Runs the shortcut actions that need the support view of the story. */
async function runShortcut(code: string): Promise<void> {
  const story = await fetchSupportStory()
  if (code === 'KeyO') {
    await showLinkOnStage('opens')
    return
  }
  const index = Number(code.replace('Digit', '')) - 1
  const step = story.steps?.find((row) => row.step_id === story.step_id)
  const link = step?.also_opens[index]
  if (!link) {
    toast.info(`This step has no Show on stage link ${index + 1}.`)
    return
  }
  await showLinkOnStage(link.id)
}

function ActiveFollower({ mode, freshOpen }: { mode: StageMode; freshOpen: boolean }) {
  const router = useRouter()
  const pathname = usePathname()
  const { story } = useStory()
  const lastSeq = useRef<number | null>(null)
  const lastCueAt = useRef(0)
  // The story as this window last polled it: Continue carries its token. One press at a time.
  const storyRef = useRef<StoryState | null>(null)
  const continuing = useRef(false)
  useEffect(() => {
    storyRef.current = story
  }, [story])

  const go = useCallback(
    (target: StageTarget | null) => {
      if (!target) return
      const href = safeAppHref(target.href)
      if (!href) return
      // The Presenter page drives its own window in single-window mode.
      if (mode === 'single' && window.location.pathname === PRESENTER_PATH) return
      if (getChosenPersona() !== target.persona) setPersona(target.persona as PersonaId)
      if (currentHref() !== href) router.push(href)
    },
    [mode, router]
  )

  // Follow the server: on the first answer only when the tab was just opened, then whenever seq grows.
  useEffect(() => {
    if (!story) return
    const seq = story.seq ?? 0
    const first = lastSeq.current === null
    const grew = !first && seq > (lastSeq.current ?? 0)
    lastSeq.current = seq
    if ((first && freshOpen) || grew) {
      const stage = story.stage && safeAppHref(story.stage.href) ? (story.stage as StageTarget) : null
      go(stage ?? (story.opens ? { href: story.opens, persona: story.persona } : null))
    }
  }, [story, freshOpen, go])

  // Follow a cue from the Presenter tab at once (storage event fires in other tabs of the profile only).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STAGE_CUE_KEY) return
      const cue = parseCue(event.newValue)
      if (!cue || cue.at <= lastCueAt.current) return
      lastCueAt.current = cue.at
      if (cue.seq > (lastSeq.current ?? -1)) lastSeq.current = cue.seq
      go({ href: cue.href, persona: cue.persona })
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [go])

  // Heartbeat, so the Presenter knows a stage window is open (the stage tab only).
  useEffect(() => {
    if (mode !== '1') return
    writeHeartbeat()
    const timer = setInterval(writeHeartbeat, STAGE_HEARTBEAT_MS)
    return () => clearInterval(timer)
  }, [mode])

  // Shortcuts.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isStageShortcut(event)) return
      // A held key repeats; only the first press counts, so holding Shift+Alt+Right never chains Continue across steps.
      if (event.repeat) {
        event.preventDefault()
        return
      }
      const code = event.code
      if (code === 'KeyP' && mode === 'single') {
        event.preventDefault()
        router.push(PRESENTER_PATH)
        return
      }
      // On the Presenter page itself the page handles its own shortcuts.
      if (pathname === PRESENTER_PATH) return
      if (code === 'ArrowRight') {
        event.preventDefault()
        if (continuing.current) return
        continuing.current = true
        void continueFromStage(storyRef.current).finally(() => {
          continuing.current = false
        })
        return
      }
      if (code === 'KeyO' || /^Digit[1-4]$/.test(code)) {
        event.preventDefault()
        runShortcut(code).catch((err) => toast.error(errorText(err)))
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, pathname, router])

  return null
}

export function StageFollower(): ReactElement | null {
  const searchParams = useSearchParams()
  const [state, setState] = useState<{ mode: StageMode | null; freshOpen: boolean }>({ mode: null, freshOpen: false })
  const stageParam = searchParams?.get('stage')

  useEffect(() => {
    const opened = stageParam === '1' || window.name === STAGE_WINDOW_NAME
    if (opened && getStageMode() !== '1') setStageMode('1')
    setState({ mode: getStageMode(), freshOpen: stageParam === '1' })
  }, [stageParam])

  // The Presenter switches this window into or out of single-window mode.
  useEffect(() => {
    const onMode = () => setState((prev) => ({ ...prev, mode: getStageMode() }))
    window.addEventListener(STAGE_MODE_EVENT, onMode)
    return () => window.removeEventListener(STAGE_MODE_EVENT, onMode)
  }, [])

  if (!state.mode) return null
  return <ActiveFollower mode={state.mode} freshOpen={state.freshOpen} />
}
